import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { performLogout } from "../../store/slices/authSlice";
import {
  LayoutDashboard,
  UserPlus,
  Hospital,
  Package,
  ClipboardList,
  TestTube,
  FileText,
  Inbox,
  User,
  History,
  TrendingUp,
  Users,
  Settings,
  Building2,
  Activity,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FlaskConical,
  Cloud,
} from "lucide-react";

const Sidebar = ({
  activeTab,
  setActiveTab,
  sidebarOpen,
  setSidebarOpen,
  variant = "desktop",
  onRequestClose,
}) => {
  const { user } = useSelector((state) => state.auth);
  const clinic = useSelector((state) => state.clinic.clinic);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [adminSectionsOpen, setAdminSectionsOpen] = React.useState({
    admin: true,
    receptionist: true,
    laboratory: true,
    pathologist: true,
  });

  const handleLogout = () => {
    dispatch(performLogout());
    navigate("/");
    if (typeof onRequestClose === "function") onRequestClose();
  };

  const getSidebarItems = () => {
    switch (user?.role) {
      case "receptionist":
        return [
          {
            icon: LayoutDashboard,
            label: "Dashboard",
            id: "rec-dashboard",
            path: "/receptionist/dashboard",
          },
          {
            icon: UserPlus,
            label: "Register Patient",
            id: "rec-register",
            path: "/receptionist/register-patient",
          },
          {
            icon: Hospital,
            label: "Order Tests",
            id: "rec-search",
            path: "/receptionist/search-patient",
          },
          {
            icon: Package,
            label: "Test Orders",
            id: "rec-orders",
            path: "/receptionist/test-order-list",
          },
          {
            icon: ClipboardList,
            label: "Visits",
            id: "rec-visits",
            path: "/receptionist/visits",
          },
        ];
      case "laboratorist":
        return [
          // {
          //   icon: LayoutDashboard,
          //   label: "Dashboard",
          //   id: "dashboard",
          //   path: "/laboratory/dashboard",
          // },
          {
            icon: FlaskConical,
            label: "Workbench",
            id: "lab-workbench",
            path: "/laboratory/workbench",
          },
          {
            icon: FileText,
            label: "Enter Results",
            id: "lab-enter-results",
            path: "/laboratory/enter-results",
          },
        ];
      case "pathologist":
        return [
          {
            icon: Inbox,
            label: "Queue",
            id: "pathologist-queue",
            path: "/pathologist/queue",
          },
          {
            icon: FileText,
            label: "Pathologist Reports",
            id: "pathologist-reports",
            path: "/reports/pathologist",
          },
        ];
      case "patient":
        return [
          {
            icon: LayoutDashboard,
            label: "Dashboard",
            id: "patient-dashboard",
            path: "/patient/dashboard",
          },
          {
            icon: User,
            label: "My Profile",
            id: "patient-profile",
            path: "/patient/profile",
          },
          {
            icon: History,
            label: "Test History",
            id: "patient-history",
            path: "/patient/history",
          },
        ];
      case "admin":
        return [
          {
            icon: LayoutDashboard,
            label: "Dashboard",
            id: "admin-dashboard",
            path: "/admin/dashboard",
          },
          {
            icon: TrendingUp,
            label: "Analytics",
            id: "admin-analytics",
            path: "/admin/analytics",
          },
          {
            icon: Cloud,
            label: "Sync Center",
            id: "admin-sync",
            path: "/admin/sync",
          },
          {
            icon: Users,
            label: "User Management",
            id: "admin-users",
            path: "/admin/users",
          },
          {
            icon: TestTube,
            label: "Test Management",
            id: "admin-tests",
            path: "/admin/tests",
          },
          {
            icon: Settings,
            label: "Laboratory Config",
            id: "admin-system",
            path: "/admin/system-settings",
          },
          {
            icon: Building2,
            label: "Clinic Settings",
            id: "admin-clinic",
            path: "/admin/clinic-settings",
          },
          {
            icon: Activity,
            label: "Activity Logs",
            id: "admin-activity",
            path: "/admin/activity-logs",
          },
          {
            icon: FileText,
            label: "Pathologist Reports",
            id: "pathologist-reports",
            path: "/reports/pathologist",
          },
        ];

      default:
        return [];
    }
  };

  const sidebarItems = getSidebarItems();

  const isAdmin = user?.role === "admin";
  const adminGroups = React.useMemo(() => {
    if (!isAdmin) return [];
    return [
      {
        key: "admin",
        label: "Admin",
        items: [
          {
            icon: LayoutDashboard,
            label: "Dashboard",
            id: "admin-dashboard",
            path: "/admin/dashboard",
          },
          {
            icon: TrendingUp,
            label: "Analytics",
            id: "admin-analytics",
            path: "/admin/analytics",
          },
          {
            icon: Cloud,
            label: "Sync Center",
            id: "admin-sync",
            path: "/admin/sync",
          },
          {
            icon: Users,
            label: "User Management",
            id: "admin-users",
            path: "/admin/users",
          },
          {
            icon: TestTube,
            label: "Test Management",
            id: "admin-tests",
            path: "/admin/tests",
          },
          {
            icon: Settings,
            label: "Laboratory Config",
            id: "admin-system",
            path: "/admin/system-settings",
          },
          {
            icon: Building2,
            label: "Clinic Settings",
            id: "admin-clinic",
            path: "/admin/clinic-settings",
          },
          {
            icon: Activity,
            label: "Activity Logs",
            id: "admin-activity",
            path: "/admin/activity-logs",
          },
        ],
      },
      {
        key: "receptionist",
        label: "Reception",
        items: [
          {
            icon: LayoutDashboard,
            label: "Dashboard",
            id: "rec-dashboard",
            path: "/receptionist/dashboard",
          },
          {
            icon: UserPlus,
            label: "Register Patient",
            id: "rec-register",
            path: "/receptionist/register-patient",
          },
          {
            icon: Hospital,
            label: "Order Tests",
            id: "rec-search",
            path: "/receptionist/search-patient",
          },
          {
            icon: Package,
            label: "Test Orders",
            id: "rec-orders",
            path: "/receptionist/test-order-list",
          },
          {
            icon: ClipboardList,
            label: "Visits",
            id: "rec-visits",
            path: "/receptionist/visits",
          },
        ],
      },
      {
        key: "laboratory",
        label: "Laboratory",
        items: [
          {
            icon: FlaskConical,
            label: "Workbench",
            id: "lab-workbench",
            path: "/laboratory/workbench",
          },
          {
            icon: FileText,
            label: "Enter Results",
            id: "lab-enter-results",
            path: "/laboratory/enter-results",
          },
        ],
      },
      {
        key: "pathologist",
        label: "Pathology",
        items: [
          {
            icon: Inbox,
            label: "Queue",
            id: "pathologist-queue",
            path: "/pathologist/queue",
          },
          {
            icon: FileText,
            label: "Reports",
            id: "pathologist-reports",
            path: "/reports/pathologist",
          },
        ],
      },
    ];
  }, [isAdmin]);

  const adminFlatItems = React.useMemo(() => {
    if (!isAdmin) return [];
    const seen = new Set();
    const all = [];
    for (const g of adminGroups) {
      for (const item of g.items) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        all.push(item);
      }
    }
    return all;
  }, [adminGroups, isAdmin]);

  const renderNavItem = (item, opts = {}) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    const indentClass = opts.indent ? "pl-8" : "";

    return (
      <button
        key={item.id}
        onClick={() => {
          navigate(item.path);
          if (typeof onRequestClose === "function") onRequestClose();
        }}
        className={`w-full flex items-center px-4 py-3.5 ${indentClass} rounded-xl text-left transition-all duration-200 group relative ${
          isActive
            ? "bg-gradient-to-r from-[#36F1A2] to-[#00d084] text-[#1a4a5a] shadow-lg scale-105"
            : "hover:bg-white/10 hover:scale-102 text-white/90 hover:text-white"
        }`}
      >
        {isActive && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#36F1A2] to-[#00d084] animate-pulse opacity-20"></div>
        )}
        <Icon
          className={`${sidebarOpen ? "w-5 h-5" : "w-6 h-6"} transition-all duration-200 ${
            isActive
              ? "text-[#1a4a5a]"
              : "text-[#36F1A2] group-hover:text-white"
          } relative z-10`}
        />
        {sidebarOpen && (
          <span
            className={`ml-4 font-medium text-sm relative z-10 ${
              isActive ? "text-[#1a4a5a]" : "text-white"
            }`}
          >
            {item.label}
          </span>
        )}
        {!sidebarOpen && (
          <div className="absolute left-full ml-6 px-3 py-2 bg-[#1a4a5a] text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-xl z-50">
            {item.label}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-8 border-transparent border-r-[#1a4a5a]"></div>
          </div>
        )}
      </button>
    );
  };

  return (
    <div
      className={`${
        variant === "mobile" ? "w-80 max-w-[85vw]" : sidebarOpen ? "w-64" : "w-20"
      } bg-gradient-to-b from-[#1a4a5a] to-[#235F72] text-white transition-all duration-300 flex flex-col shadow-2xl relative custom-scrollbar ${
        variant === "desktop" ? "sticky top-0 h-screen overflow-hidden self-start" : "h-full"
      }`}
    >
      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          transition: background 0.3s ease;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        .nav-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .nav-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .nav-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 10px;
        }
        .nav-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.25);
        }
      `}</style>
      
      {/* Logo Section */}
      <div className="p-6 flex items-center justify-between border-b border-white/10">
        {sidebarOpen && (
          <div className="flex items-center space-x-3 animate-fadeIn">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg overflow-hidden">
              <img
                src={clinic?.logo_url || "/logo.png"}
                alt={clinic?.name ? `${clinic.name} Logo` : "Clinic Logo"}
                className="w-8 h-8 object-contain"
                onError={(e) => { e.target.src = "/logo.png"; }}
              />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sm leading-tight">{clinic?.name || "World Basic"}</span>
              <span className="text-xs text-[#36F1A2] font-medium">Laboratory</span>
            </div>
          </div>
        )}
        {!sidebarOpen && (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#36F1A2] to-[#00d084] flex items-center justify-center shadow-lg mx-auto overflow-hidden">
            <img
              src={clinic?.logo_url || "/logo.png"}
              alt={clinic?.name ? `${clinic.name} Logo` : "Clinic Logo"}
              className="w-8 h-8 object-contain"
              onError={(e) => { e.target.src = "/logo.png"; }}
            />
          </div>
        )}
        {variant === "desktop" ? (
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-white/10 p-2 rounded-lg transition-all duration-200 hover:scale-110 ml-auto"
            aria-label="Toggle Sidebar"
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        ) : null}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto nav-scrollbar">
        {isAdmin && sidebarOpen ? (
          <div className="space-y-3">
            {adminGroups.map((group) => {
              const open = !!adminSectionsOpen[group.key];
              return (
                <div key={group.key} className="rounded-xl bg-white/5 ring-1 ring-white/10">
                  <button
                    type="button"
                    onClick={() =>
                      setAdminSectionsOpen((prev) => ({
                        ...prev,
                        [group.key]: !prev[group.key],
                      }))
                    }
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-white/80">
                      {group.label}
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 text-white/70 transition-transform ${
                        open ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>

                  {open ? (
                    <div className="px-1 pb-2 space-y-1">
                      {group.items.map((item) => renderNavItem(item, { indent: true }))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          (isAdmin ? adminFlatItems : sidebarItems).map((item) => renderNavItem(item))
        )}
      </nav>

      {/* Logout Section */}
      <div className="p-4 border-t border-white/10">
        <button
          onClick={handleLogout}
          className={`w-full flex items-center justify-center ${
            sidebarOpen ? "px-4" : "px-2"
          } py-3 rounded-xl bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white transition-all duration-200 group hover:scale-105 shadow-lg`}
        >
          <LogOut className="w-5 h-5" />
          {sidebarOpen && <span className="ml-3 font-medium text-sm">Logout</span>}
        </button>

        {sidebarOpen ? (
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-white/70">
            <img
              src="/Bewide-logo.png"
              alt="BeWide Technologies"
              className="h-6 w-auto object-contain"
            />
            <span>developed by Bewide Technologies</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Sidebar;
